import apiService from './apiService';

// Re-export the shared formatter helpers from dmarcApi — same shapes, same
// behavior. Keeps a single source of truth.
export { fmtPct, fmtNumber, fmtRelative, fmtEpoch } from './dmarcApi';

// Types mirror the FastAPI response shape (api/services/tlsrpt_service.py).

export interface DomainSummary {
  domain: string;
  report_count_30d: number;
  report_count_7d: number;
  successful_sessions_30d: number;
  failure_sessions_30d: number;
  successful_sessions_7d: number;
  failure_sessions_7d: number;
  failure_rate_30d: number | null;
  failure_rate_prior_30d: number | null;
  trend: 'up' | 'down' | 'flat' | null; // up=worsening failures, down=improving
  enforce_ready: boolean;
  enforce_ready_window_days: number;
  failures_in_enforce_window: number;
  last_seen_epoch: number | null;
  last_seen_iso: string | null;
}

export interface TimeseriesPoint {
  date: string;                       // YYYY-MM-DD
  successful_sessions: number;
  failure_sessions: number;
  total_sessions: number;
  failure_rate: number | null;
}

export interface FailureTypeRow {
  result_type: string;                // RFC 8460 result_type token
  receiving_mx_hostname: string;      // (any) if not reported
  failure_sessions: number;
  sending_mta_ip_count: number;
  sample_sending_mta_ips: string[];
  sample_additional_information: string;
}

export interface ReporterRow {
  organization: string;
  reports: number;
  successful_sessions: number;
  failure_sessions: number;
  failure_rate: number | null;
  last_seen_epoch: number;
  last_seen_iso: string;
}

export interface ReportListItem {
  domain: string;
  report_uid: string;
  organization: string;
  date_begin: number;                 // unix epoch
  date_end: number;
  total_successful: number;
  total_failure: number;
}

export interface FailureDetail {
  result_type: string;
  sending_mta_ip: string;
  receiving_mx_hostname: string;
  receiving_ip: string;
  failed_session_count: number;
  additional_information: string;
}

export interface PolicyBlock {
  policy_type: string;                // sts | tlsa | no-policy-found
  policy_string: string[];
  policy_domain: string;
  mx_hosts: string[];
  successful_count: number;
  failure_count: number;
  failures: FailureDetail[];
}

export interface TlsRptReport {
  domain: string;
  report_uid: string;
  organization: string;
  report_id: string;
  contact_info: string;
  date_begin: number;
  date_end: number;
  total_successful: number;
  total_failure: number;
  policies: PolicyBlock[];
  raw_s3_key?: string;
  processed_at?: string;
}

export interface DomainDetailResponse {
  summary: DomainSummary;
  timeseries: TimeseriesPoint[];
  top_failure_types: FailureTypeRow[];
  top_reporters: ReporterRow[];
}

export const tlsrptApi = {
  listDomains: () =>
    apiService
      .get<{ domains: DomainSummary[] }>('/api/admin/tlsrpt/domains')
      .then(r => r.data),
  getDomain: (domain: string, days = 30) =>
    apiService
      .get<DomainDetailResponse>(
        `/api/admin/tlsrpt/domains/${encodeURIComponent(domain)}?days=${days}`,
      )
      .then(r => r.data),
  listReports: (domain: string, limit = 50) =>
    apiService
      .get<{ reports: ReportListItem[] }>(
        `/api/admin/tlsrpt/domains/${encodeURIComponent(domain)}/reports?limit=${limit}`,
      )
      .then(r => r.data),
  getReport: (domain: string, reportUid: string) =>
    apiService
      .get<TlsRptReport>(
        `/api/admin/tlsrpt/domains/${encodeURIComponent(domain)}/reports/${encodeURIComponent(reportUid)}`,
      )
      .then(r => r.data),
  listMonitored: () =>
    apiService
      .get<{ domains: string[] }>('/api/admin/tlsrpt/monitored-domains')
      .then(r => r.data),
};
